'use client';

import { useCallback, useMemo, useState } from 'react';
import { X } from '@phosphor-icons/react';
import {
  BriefcaseIcon,
  HomeIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from '@/components/icons';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/primitives';
import { Input } from '@/components/ui/inputs';
import { useAddressBook, useAddressBookMutations } from '@/hooks/use-address-book';
import { useAddressStore } from '@/stores/address-store';
import {
  ADDRESS_TYPE_LABELS,
  MAX_SAVED_ADDRESSES,
  type AddressType,
  type SavedAddress,
  type SavedAddressInput,
} from '@/lib/api/addresses';

/**
 * Адресная книга в профиле (вопрос 16, ответ «А»).
 *
 * Раньше вкладка «Мои адреса» открывала форму имени и телефона — сохранённых
 * адресов не было вовсе, хотя интерфейс их обещал. Теперь здесь до пяти адресов
 * с ярлыками «Дом», «Работа» и произвольными, и подъезд с этажом и домофоном
 * запоминаются вместе с адресом, а не спрашиваются заново в корзине.
 *
 * Строку адреса руками не пишут: её выбирают на карте — там же, где её
 * подтверждают при заказе, и там же проверяется зона доставки. Свободный ввод
 * дал бы адрес без координат, по которому потом невозможно посчитать доставку.
 */

const TYPE_ICONS: Record<AddressType, React.ElementType> = {
  home: HomeIcon,
  work: BriefcaseIcon,
  other: MapPinIcon,
};

const TYPE_ORDER: AddressType[] = ['home', 'work', 'other'];

interface FormState {
  id: number | null;
  label: string;
  address: string;
  type: AddressType;
  apartment: string;
  entrance: string;
  floor: string;
  intercom: string;
  comment: string;
  isPrivateHouse: boolean;
  hasIntercom: boolean;
  isDefault: boolean;
  lat: number | null;
  lon: number | null;
}

const EMPTY_FORM: FormState = {
  id: null,
  label: '',
  address: '',
  type: 'home',
  apartment: '',
  entrance: '',
  floor: '',
  intercom: '',
  comment: '',
  isPrivateHouse: false,
  hasIntercom: true,
  isDefault: false,
  lat: null,
  lon: null,
};

function fromSaved(saved: SavedAddress): FormState {
  return {
    id: saved.id,
    label: saved.label,
    address: saved.address,
    type: saved.type,
    apartment: saved.apartment,
    entrance: saved.entrance,
    floor: saved.floor,
    intercom: saved.intercom,
    comment: saved.comment,
    isPrivateHouse: saved.is_private_house === 1,
    hasIntercom: saved.has_intercom !== 0,
    isDefault: saved.is_default === 1,
    lat: saved.lat,
    lon: saved.lon,
  };
}

function toInput(form: FormState): SavedAddressInput {
  return {
    label: form.label.trim() || ADDRESS_TYPE_LABELS[form.type],
    address: form.address.trim(),
    type: form.type,
    apartment: form.isPrivateHouse ? '' : form.apartment.trim(),
    entrance: form.isPrivateHouse ? '' : form.entrance.trim(),
    floor: form.isPrivateHouse ? '' : form.floor.trim(),
    intercom: form.isPrivateHouse || !form.hasIntercom ? '' : form.intercom.trim(),
    comment: form.comment.trim(),
    is_private_house: form.isPrivateHouse,
    has_intercom: form.hasIntercom,
    is_default: form.isDefault,
    lat: form.lat,
    lon: form.lon,
  };
}

export function AddressBook() {
  const { addresses, isLoading } = useAddressBook();
  const { create, update, remove } = useAddressBookMutations();
  const openAddressSheet = useAddressStore((state) => state.openSheet);

  const [form, setForm] = useState<FormState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  const isFull = addresses.length >= MAX_SAVED_ADDRESSES;
  const isSaving = create.isPending || update.isPending;

  const patch = useCallback((changes: Partial<FormState>) => {
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));
  }, []);

  const handleAdd = useCallback(() => {
    // Адрес берём из того, что уже выбрано на карте: это единственный источник
    // строки с координатами. Пусто — отправляем человека выбирать.
    const selected = useAddressStore.getState().selectedAddress;
    setForm({
      ...EMPTY_FORM,
      address: selected?.address ?? '',
      lat: selected?.coordinates?.lat ?? null,
      lon: selected?.coordinates?.lon ?? null,
      isDefault: addresses.length === 0,
    });
  }, [addresses.length]);

  const handlePickOnMap = useCallback(() => {
    // Карта открывается поверх профиля; после подтверждения адрес окажется
    // в address-store, и форма подхватит его следующим нажатием «Взять с карты».
    openAddressSheet();
  }, [openAddressSheet]);

  const handlePullFromMap = useCallback(() => {
    const selected = useAddressStore.getState().selectedAddress;
    if (!selected?.address) {
      toast.error('Сначала выберите адрес на карте');
      return;
    }
    patch({
      address: selected.address,
      lat: selected.coordinates?.lat ?? null,
      lon: selected.coordinates?.lon ?? null,
    });
  }, [patch]);

  const handleSubmit = useCallback(async () => {
    if (!form) return;
    if (!form.address.trim()) {
      toast.error('Укажите адрес');
      return;
    }
    if (!form.isPrivateHouse && !form.apartment.trim()) {
      toast.error('Укажите номер квартиры');
      return;
    }

    try {
      if (form.id == null) {
        await create.mutateAsync(toInput(form));
        toast.success('Адрес сохранён');
      } else {
        await update.mutateAsync({ id: form.id, input: toInput(form) });
        toast.success('Адрес обновлён');
      }
      setForm(null);
    } catch {
      toast.error('Не удалось сохранить адрес');
    }
  }, [form, create, update]);

  const handleDelete = useCallback(async (id: number) => {
    try {
      await remove.mutateAsync(id);
      setPendingDelete(null);
      toast.success('Адрес удалён');
    } catch {
      toast.error('Не удалось удалить адрес');
    }
  }, [remove]);

  const sorted = useMemo(
    () => [...addresses].sort((a, b) => b.is_default - a.is_default),
    [addresses]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 rounded-pill border-2 border-[var(--primary)] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-[var(--text-secondary)]">
          До {MAX_SAVED_ADDRESSES} адресов. Подъезд, этаж и домофон сохраняются вместе с адресом —
          при заказе их не спросят заново.
        </p>
        {!form && (
          <Button size="sm" onClick={handleAdd} disabled={isFull} className="flex-shrink-0">
            <PlusIcon className="h-4 w-4" weight="bold" />
            Добавить
          </Button>
        )}
      </div>

      {isFull && !form && (
        <p className="text-xs text-[var(--text-tertiary)]">
          Достигнут лимит адресов. Удалите ненужный, чтобы добавить новый.
        </p>
      )}

      {form && (
        <div className="rounded-card border border-[var(--border-default)] bg-[var(--surface-secondary)] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-[var(--text-primary)]">
              {form.id == null ? 'Новый адрес' : 'Редактирование адреса'}
            </h4>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              aria-label="Закрыть форму"
            >
              <X className="h-5 w-5" weight="bold" />
            </button>
          </div>

          {/* Тип адреса — «Дом», «Работа», «Другое» */}
          <div className="flex gap-2">
            {TYPE_ORDER.map((type) => {
              const Icon = TYPE_ICONS[type];
              const active = form.type === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => patch({ type })}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 rounded-pill border px-3 py-2.5 text-sm font-medium transition-colors',
                    active
                      ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                      : 'border-[var(--border-default)] bg-[var(--surface-primary)] text-[var(--text-secondary)]'
                  )}
                >
                  <Icon className="h-4 w-4" weight={active ? 'fill' : 'regular'} />
                  {ADDRESS_TYPE_LABELS[type]}
                </button>
              );
            })}
          </div>

          <Input
            id="address-label"
            label="Название"
            value={form.label}
            onChange={(e) => patch({ label: e.target.value })}
            placeholder={ADDRESS_TYPE_LABELS[form.type]}
          />

          {/* Строка адреса только для чтения: печатать её руками нельзя —
              без координат зону доставки посчитать не по чему. */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--text-secondary)]">
              Адрес *
            </label>
            <div className="flex items-center gap-2 rounded-card border border-[var(--border-default)] bg-[var(--surface-primary)] px-3 py-2.5">
              <MapPinIcon className="h-5 w-5 flex-shrink-0 text-[var(--text-tertiary)]" weight="fill" />
              <p
                className={cn(
                  'min-w-0 flex-1 text-sm',
                  form.address ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
                )}
              >
                {form.address || 'Адрес не выбран'}
              </p>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={handlePickOnMap}>
                Выбрать на карте
              </Button>
              <Button size="sm" variant="ghost" onClick={handlePullFromMap}>
                Взять выбранный
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={form.isPrivateHouse}
              onChange={(e) => patch({ isPrivateHouse: e.target.checked })}
              className="h-4 w-4 accent-[var(--primary)]"
            />
            Частный дом
          </label>

          {!form.isPrivateHouse && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  id="address-apartment"
                  label="Квартира *"
                  value={form.apartment}
                  onChange={(e) => patch({ apartment: e.target.value })}
                />
                <Input
                  id="address-entrance"
                  label="Подъезд"
                  value={form.entrance}
                  onChange={(e) => patch({ entrance: e.target.value })}
                />
                <Input
                  id="address-floor"
                  label="Этаж"
                  value={form.floor}
                  onChange={(e) => patch({ floor: e.target.value })}
                />
                <Input
                  id="address-intercom"
                  label="Домофон"
                  value={form.intercom}
                  onChange={(e) => patch({ intercom: e.target.value })}
                  disabled={!form.hasIntercom}
                />
              </div>

              {/* Одно нажатие для домов с открытым подъездом (вопрос 15) */}
              <button
                type="button"
                onClick={() => patch({ hasIntercom: !form.hasIntercom, intercom: '' })}
                className={cn(
                  'w-full rounded-card border px-3 py-2.5 text-sm font-medium transition-colors',
                  form.hasIntercom
                    ? 'border-[var(--border-default)] bg-[var(--surface-primary)] text-[var(--text-secondary)]'
                    : 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                )}
              >
                {form.hasIntercom ? 'Домофона нет' : 'Домофон есть'}
              </button>
            </>
          )}

          <Input
            id="address-comment"
            label="Комментарий курьеру"
            value={form.comment}
            onChange={(e) => patch({ comment: e.target.value })}
          />

          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => patch({ isDefault: e.target.checked })}
              className="h-4 w-4 accent-[var(--primary)]"
            />
            Подставлять этот адрес при заказе
          </label>

          <div className="flex gap-2">
            <Button onClick={() => void handleSubmit()} disabled={isSaving} className="flex-1">
              {isSaving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
            <Button variant="outline" onClick={() => setForm(null)} disabled={isSaving}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      {sorted.length === 0 && !form && (
        <div className="rounded-card border border-dashed border-[var(--border-default)] py-12 text-center">
          <MapPinIcon className="mx-auto h-10 w-10 text-[var(--text-tertiary)]" weight="duotone" />
          <p className="mt-3 text-sm font-medium text-[var(--text-primary)]">
            Сохранённых адресов пока нет
          </p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">
            Добавьте адрес — и при следующем заказе не придётся вводить его заново
          </p>
        </div>
      )}

      <div className="space-y-3">
        {sorted.map((saved) => {
          const Icon = TYPE_ICONS[saved.type] ?? MapPinIcon;
          return (
            <div
              key={saved.id}
              className="rounded-card border border-[var(--border-default)] bg-[var(--surface-primary)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-pill bg-[var(--primary)]/10">
                    <Icon className="h-5 w-5 text-[var(--primary)]" weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[var(--text-primary)]">
                        {saved.label || ADDRESS_TYPE_LABELS[saved.type]}
                      </span>
                      {saved.is_default === 1 && (
                        <span className="inline-flex items-center gap-1 rounded-pill bg-[var(--primary)]/10 px-2 py-0.5 t-nano text-[var(--primary)]">
                          <StarIcon className="h-3 w-3" weight="fill" />
                          Основной
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-[var(--text-secondary)] break-words">
                      {saved.address}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {saved.apartment && <Chip>Кв. {saved.apartment}</Chip>}
                      {saved.entrance && <Chip>Подъезд {saved.entrance}</Chip>}
                      {saved.floor && <Chip>Этаж {saved.floor}</Chip>}
                      {saved.intercom && <Chip>Домофон {saved.intercom}</Chip>}
                      {saved.has_intercom === 0 && <Chip>Без домофона</Chip>}
                      {saved.is_private_house === 1 && <Chip>Частный дом</Chip>}
                    </div>
                    {saved.comment && (
                      <p className="mt-2 text-xs italic text-[var(--text-tertiary)]">
                        {saved.comment}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 gap-1">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => setForm(fromSaved(saved))}
                    aria-label="Редактировать адрес"
                  >
                    <PencilIcon className="h-4 w-4" weight="bold" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => setPendingDelete(saved.id)}
                    aria-label="Удалить адрес"
                  >
                    <TrashIcon className="h-4 w-4 text-[var(--error)]" weight="bold" />
                  </Button>
                </div>
              </div>

              {pendingDelete === saved.id && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-card bg-[var(--error)]/10 px-3 py-2">
                  <span className="text-xs font-medium text-[var(--error)]">Удалить этот адрес?</span>
                  <div className="flex gap-2">
                    <Button
                      size="xs"
                      variant="destructive"
                      onClick={() => void handleDelete(saved.id)}
                      disabled={remove.isPending}
                    >
                      Удалить
                    </Button>
                    <Button size="xs" variant="ghost" onClick={() => setPendingDelete(null)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-pill bg-[var(--surface-secondary)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">
      {children}
    </span>
  );
}
